import React, { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Layers, Wand2, Film, Mic, Music2, Sparkles, Download, Loader2, Play, ChevronRight,
} from "lucide-react";
import { useProjectPolling } from "../lib/useProjectPolling";

export default function Composer() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [sourceId, setSourceId] = useState("");
  const [voiceId, setVoiceId] = useState("");
  const [trackId, setTrackId] = useState("");
  const [musicVolume, setMusicVolume] = useState(0.18);
  const [kenBurns, setKenBurns] = useState(true);
  const [title, setTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { project: polledProject, polling, error: pollError, start: startPoll, reset: resetPoll } = useProjectPolling();
  const loading = submitting || polling;
  const result = polledProject?.status === "completed" ? polledProject : null;

  useEffect(() => {
    (async () => {
      try {
        const [p, m] = await Promise.all([api.get("/projects"), api.get("/music/tracks")]);
        setProjects(p.data);
        setTracks(m.data.tracks);
      } catch (err) {
        console.warn("Composer initial load failed:", err?.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flyers = useMemo(() => projects.filter(p => p.kind === "flyer"), [projects]);
  const videos = useMemo(() => projects.filter(p => p.kind === "video" || (p.kind === "ad" && p.subkind === "sequence")), [projects]);
  const voices = useMemo(() => projects.filter(p => p.kind === "voice"), [projects]);

  const tracksByMood = useMemo(() => {
    const groups = {};
    tracks.forEach(t => { (groups[t.mood] = groups[t.mood] || []).push(t); });
    return groups;
  }, [tracks]);

  const canCompose = !!sourceId;

  const submit = async () => {
    if (!canCompose) { toast.error("Choisissez au moins un visuel source"); return; }
    setSubmitting(true); resetPoll();
    try {
      const { data } = await api.post("/ad/compose", {
        source_project_id: sourceId,
        voice_project_id: voiceId || undefined,
        music_track_id: trackId || undefined,
        music_volume: musicVolume,
        ken_burns: kenBurns,
        title: title || undefined,
      });
      if (!data?.project_id) throw new Error("Pas de project_id renvoyé");
      toast.info("Assemblage en cours, 30s à 3 min...");
      startPoll(data.project_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur composition");
    } finally { setSubmitting(false); }
  };

  useEffect(() => {
    if (polledProject?.status === "completed") toast.success("Pub finale assemblée !");
    if (polledProject?.status === "failed") toast.error(pollError || "Assemblage échoué");
  }, [polledProject?.status, pollError]);

  const download = () => {
    if (!result?.data_url) return;
    const a = document.createElement("a");
    a.href = result.data_url; a.download = `xookpub-pub-finale-${Date.now()}.mp4`; a.click();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="label-mono">Studio · Assembleur final</span>
          <h1 className="mt-2 text-4xl font-heading tracking-tighter">
            Assemblez votre <span className="gold-text">pub finale</span> en un clic
          </h1>
          <p className="mt-2 text-white/55 max-w-2xl">Mariez un visuel (flyer ou vidéo), votre voix off et une musique de fond. Notre moteur ffmpeg crée la pub MP4 prête à diffuser.</p>
        </div>
        <Layers className="w-10 h-10 text-gold" />
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Source picker */}
        <div className="lg:col-span-7 space-y-5">
          <div className="studio-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <Wand2 className="w-4 h-4 text-gold" /> <span className="label-mono">1. Choisissez un visuel</span>
            </div>
            {flyers.length === 0 && videos.length === 0 ? (
              <EmptyHint onCreate={() => navigate("/studio/flyer")} text="Aucun flyer ou vidéo enregistré. Créez-en un d'abord." />
            ) : (
              <div className="space-y-4">
                {flyers.length > 0 && (
                  <Section title="Flyers" icon={Wand2}>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {flyers.map(f => (
                        <button key={f.project_id} data-testid={`pick-source-${f.project_id}`} onClick={() => setSourceId(f.project_id)}
                          className={`text-left rounded-xl overflow-hidden border-2 transition ${sourceId===f.project_id ? "border-gold" : "border-white/5 hover:border-white/20"}`}>
                          <div className="aspect-square bg-obsidian">
                            {f.thumbnail_url && <img src={f.thumbnail_url} alt={f.title} className="w-full h-full object-cover" />}
                          </div>
                          <div className="p-2 text-xs truncate">{f.title}</div>
                        </button>
                      ))}
                    </div>
                  </Section>
                )}
                {videos.length > 0 && (
                  <Section title="Vidéos" icon={Film}>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {videos.map(v => (
                        <button key={v.project_id} data-testid={`pick-source-${v.project_id}`} onClick={() => setSourceId(v.project_id)}
                          className={`text-left rounded-xl overflow-hidden border-2 transition ${sourceId===v.project_id ? "border-gold" : "border-white/5 hover:border-white/20"}`}>
                          <div className="aspect-video bg-obsidian flex items-center justify-center text-white/30">
                            <Film className="w-6 h-6" />
                          </div>
                          <div className="p-2 text-xs truncate">{v.title}</div>
                        </button>
                      ))}
                    </div>
                  </Section>
                )}
              </div>
            )}
          </div>

          <div className="studio-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <Mic className="w-4 h-4 text-gold" /> <span className="label-mono">2. Voix off (optionnelle)</span>
            </div>
            {voices.length === 0 ? (
              <EmptyHint onCreate={() => navigate("/studio/voice")} text="Aucune voix off enregistrée pour l'instant." />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-72 overflow-y-auto scroll-thin">
                <button onClick={() => setVoiceId("")} data-testid="pick-voice-none"
                  className={`p-3 rounded-lg border text-left text-sm ${!voiceId ? "border-gold bg-gold/10 text-gold" : "border-white/10"}`}>
                  Aucune voix
                </button>
                {voices.map(v => (
                  <button key={v.project_id} data-testid={`pick-voice-${v.project_id}`} onClick={() => setVoiceId(v.project_id)}
                    className={`p-3 rounded-lg border text-left ${voiceId===v.project_id ? "border-gold bg-gold/10" : "border-white/10 hover:border-white/30"}`}>
                    <div className="text-sm truncate">{v.title}</div>
                    {v.data_url && <audio src={v.data_url} controls className="mt-2 w-full h-8" preload="none" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="studio-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <Music2 className="w-4 h-4 text-gold" /> <span className="label-mono">3. Musique de fond (optionnelle)</span>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              <button onClick={() => setTrackId("")} data-testid="pick-track-none"
                className={`px-3 py-1.5 rounded-full text-xs border ${!trackId ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-white/70"}`}>
                Aucune
              </button>
              {Object.keys(tracksByMood).map(mood => (
                <span key={mood} className="px-3 py-1.5 rounded-full text-xs border border-white/10 text-white/40 capitalize">{mood}</span>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-80 overflow-y-auto scroll-thin">
              {tracks.map(t => (
                <button key={t.id} data-testid={`pick-track-${t.id}`} onClick={() => setTrackId(t.id)}
                  className={`p-3 rounded-lg border text-left ${trackId===t.id ? "border-gold bg-gold/10" : "border-white/10 hover:border-white/30"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="font-heading text-sm">{t.title}</div>
                      <div className="text-[10px] uppercase tracking-widest text-white/40 mt-0.5">{t.mood} · {t.bpm} BPM</div>
                    </div>
                    <Music2 className="w-4 h-4 text-gold/70" />
                  </div>
                  <audio src={t.url} controls className="mt-2 w-full h-8" preload="none" />
                </button>
              ))}
            </div>
            {trackId && (
              <div className="mt-4">
                <label className="label-mono">Volume musique : {(musicVolume*100).toFixed(0)}%</label>
                <input data-testid="music-volume" type="range" min="0" max="100" value={musicVolume*100}
                  onChange={(e) => setMusicVolume(+e.target.value/100)}
                  className="w-full accent-[#D4AF37] mt-2" />
              </div>
            )}
          </div>
        </div>

        {/* Right pane */}
        <div className="lg:col-span-5">
          <div className="studio-card p-5 sticky top-24 space-y-5">
            <div>
              <span className="label-mono">Aperçu final</span>
              <div className="mt-3 aspect-video bg-obsidian rounded-xl border border-white/5 flex items-center justify-center overflow-hidden">
                {loading ? (
                  <div className="text-center">
                    <div className="w-10 h-10 mx-auto rounded-full border-2 border-gold border-t-transparent animate-spin" />
                    <div className="mt-3 label-mono">Assemblage FFmpeg...</div>
                    <div className="text-xs text-white/40 mt-1">Quelques secondes</div>
                  </div>
                ) : result?.data_url ? (
                  <video src={result.data_url} controls className="w-full h-full" data-testid="compose-result" />
                ) : (
                  <div className="text-center text-white/30">
                    <Play className="w-10 h-10 mx-auto" />
                    <div className="mt-2 text-sm">Votre pub finale apparaîtra ici</div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <Row label="Visuel" value={projects.find(p=>p.project_id===sourceId)?.title || "—"} ok={!!sourceId} />
              <Row label="Voix off" value={voices.find(v=>v.project_id===voiceId)?.title || "Aucune"} ok={true} />
              <Row label="Musique" value={tracks.find(t=>t.id===trackId)?.title || "Aucune"} ok={true} />
            </div>

            <div>
              <label className="label-mono">Titre de la pub (optionnel)</label>
              <input data-testid="compose-title" value={title} onChange={e=>setTitle(e.target.value)} placeholder="Ex: Boutique Or - Collection Hiver"
                className="mt-2 w-full bg-obsidian-light border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-gold/50" />
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={kenBurns} onChange={e=>setKenBurns(e.target.checked)} className="accent-[#D4AF37]" data-testid="ken-burns" />
              Effet Ken Burns (zoom doux sur images)
            </label>

            <div className="flex gap-2">
              <button data-testid="compose-submit" onClick={submit} disabled={loading || !canCompose} className="gold-btn flex-1 !py-3 disabled:opacity-50">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Composition...</> : <><Sparkles className="w-4 h-4" /> Assembler</>}
              </button>
              {result?.data_url && (
                <button data-testid="compose-download" onClick={download} className="ghost-btn !py-3 !px-4">
                  <Download className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, icon: Icon, children }) => (
  <div>
    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/40 mb-2">
      <Icon className="w-3 h-3" /> {title}
    </div>
    {children}
  </div>
);

const Row = ({ label, value, ok }) => (
  <div className="flex items-center justify-between py-1">
    <span className="text-white/50">{label}</span>
    <span className={`truncate max-w-[60%] text-right ${ok ? "text-white" : "text-red-400"}`}>{value}</span>
  </div>
);

const EmptyHint = ({ text, onCreate }) => (
  <div className="p-4 rounded-lg bg-white/5 border border-white/10 text-sm flex items-center justify-between">
    <span className="text-white/60">{text}</span>
    <button onClick={onCreate} className="ghost-btn !py-1.5 !px-3 text-xs">Créer <ChevronRight className="w-3 h-3" /></button>
  </div>
);
